"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizePartList = normalizePartList;
exports.normalizePartValue = normalizePartValue;
exports.getToolCallKeyForPart = getToolCallKeyForPart;
const conventions_1 = require("../../conventions");
const json_1 = require("../utils/json");
const fallback_1 = require("./message-parts/fallback");
const media_1 = require("./message-parts/media");
const reasoning_1 = require("./message-parts/reasoning");
const text_1 = require("./message-parts/text");
const providers_1 = require("../utils/providers");
const rawToolCallKeys = new WeakMap();
/**
 * Part dispatch. Typed parts are claimed exclusively by one handler; a
 * message may still contain parts from several provider dialects. Shared
 * type names are handled once here, while provider-specific names are looked
 * up in the convention registry.
 */
// `image` and `file` are contested type names: Anthropic/AI-SDK and
// OpenAI/AI-SDK register guarded handlers for them in their own conventions;
// a handler returning `unmatched` falls through to the next provider.
const SHARED_TYPED_PART_HANDLERS = {
    text: (value) => (0, conventions_1.claimed)((0, text_1.normalizeTextPart)(value)),
    input_text: (value) => (0, conventions_1.claimed)((0, text_1.normalizeTextPart)(value)),
    output_text: (value) => (0, conventions_1.claimed)((0, text_1.normalizeTextPart)(value)),
    reasoning: (value) => (0, conventions_1.claimed)((0, reasoning_1.normalizeReasoningTextPart)(value)),
};
function createPartContext(parserContext) {
    return {
        normalizePartValue: (value) => normalizePartValue(value, parserContext),
        normalizePartList: (values) => normalizePartList(values, parserContext),
    };
}
/** Value-array -> parts mapper handed to providers as `context.normalizePartList`. */
function normalizePartList(values, parserContext) {
    const parts = [];
    for (const value of values) {
        if (typeof value === "string") {
            parts.push(...(0, media_1.normalizeMediaPartsFromString)(value));
            continue;
        }
        for (const part of normalizePartValue(value, parserContext)) {
            // Text parts frequently embed media reference tokens mid-string; split
            // them out. Refusals and annotated text stay intact.
            if (part.type === "text" && !part.providerMetadata && !part.refusal) {
                parts.push(...(0, media_1.normalizeMediaPartsFromString)(part.text));
                continue;
            }
            parts.push(part);
        }
    }
    return parts;
}
function asParts(value) {
    if (value === null)
        return [];
    return Array.isArray(value) ? value : [value];
}
function normalizePartBase(value, parserContext) {
    if (typeof value === "string") {
        const mediaReference = (0, media_1.parseMediaReference)(value);
        if (mediaReference)
            return [(0, media_1.filePartFromMediaReference)(mediaReference)];
        return [{ type: "text", text: value }];
    }
    if (!(0, json_1.isRecord)(value))
        return [];
    const partContext = createPartContext(parserContext);
    const providers = (0, providers_1.providersInOrder)(parserContext?.preferredProvider);
    const type = typeof value.type === "string" ? value.type : undefined;
    if (type) {
        for (const provider of providers) {
            const handler = (0, json_1.ownLookup)(provider.typedParts, type);
            if (!handler)
                continue;
            const result = handler(value, partContext);
            if (result.matched)
                return asParts(result.value);
        }
        const sharedHandler = (0, json_1.ownLookup)(SHARED_TYPED_PART_HANDLERS, type);
        if (sharedHandler) {
            const result = sharedHandler(value, partContext);
            if (result.matched)
                return asParts(result.value);
        }
    }
    else {
        for (const provider of providers) {
            const result = provider.tryNormalizeUntypedPart?.(value, partContext);
            if (result?.matched)
                return asParts(result.value);
        }
    }
    return asParts((0, fallback_1.normalizeFallbackPart)(value));
}
const COMMON_CONSUMED_PART_KEYS = [
    "type",
    "part_kind", // Pydantic part discriminator
    "providerMetadata",
    "providerOptions",
];
const CONSUMED_PART_KEYS_BY_TYPE = {
    text: new Set([
        ...COMMON_CONSUMED_PART_KEYS,
        "text",
        "content",
        "thought",
        "thoughtSignature",
        "thought_signature",
        "refusal",
        "citations",
        "annotations",
    ]),
    reasoning: new Set([
        ...COMMON_CONSUMED_PART_KEYS,
        "text",
        "content",
        "thinking",
        "signature",
        "data",
        "encrypted_content",
        "summary",
        "thought",
        "thoughtSignature",
        "thought_signature",
    ]),
    "tool-call": new Set([
        ...COMMON_CONSUMED_PART_KEYS,
        "id",
        "call_id",
        "toolCallId",
        "tool_call_id",
        "name",
        "toolName",
        "tool_name",
        "input",
        "arguments",
        "args",
        "function",
        "function_call",
        "functionCall",
        "custom",
        "executable_code",
        "executableCode",
        "providerExecuted",
    ]),
    "tool-result": new Set([
        ...COMMON_CONSUMED_PART_KEYS,
        "id",
        "call_id",
        "toolCallId",
        "tool_call_id",
        "tool_use_id",
        "name",
        "toolName",
        "tool_name",
        "output",
        "response",
        "result",
        "content",
        "value",
        "error",
        "is_error",
        "isError",
        "function_response",
        "functionResponse",
        "code_execution_result",
        "codeExecutionResult",
    ]),
    file: new Set([
        ...COMMON_CONSUMED_PART_KEYS,
        "image",
        "data",
        "url",
        "mediaType",
        "mimeType",
        "mime_type",
        "filename",
        "file",
        "file_id",
        "file_data",
        "file_url",
        "image_url",
        "input_image",
        "input_audio",
        "inline_data",
        "inlineData",
        "fileData",
        "source",
        "format",
    ]),
    // These fallback parts already preserve the complete raw value. Inferring a
    // remainder would duplicate it, but explicit providerOptions/metadata still
    // merge below for consistency with recognized parts.
    data: new Set(),
    custom: new Set(),
};
/**
 * Known part fields normalize into the canonical union; every unconsumed raw
 * field becomes providerMetadata. AI SDK providerOptions and explicit/parser-
 * computed metadata win over the inferred remainder.
 */
function withProviderMetadata(part, value) {
    const providerOptions = (0, json_1.asRecord)(value.providerOptions);
    const explicitProviderMetadata = (0, json_1.asRecord)(value.providerMetadata);
    const explicitMetadata = {
        ...providerOptions,
        ...explicitProviderMetadata,
        ...part.providerMetadata,
    };
    const inferRemainder = part.type !== "data" && part.type !== "custom";
    const consumedKeys = new Set(CONSUMED_PART_KEYS_BY_TYPE[part.type]);
    // Provider-executed built-ins commonly turn every remaining raw payload
    // field (action, queries, code, results, ...) into their canonical input.
    // Exclude those dynamic keys so the same payload is not duplicated in
    // providerMetadata.
    if (part.type === "tool-call" &&
        part.providerExecuted === true &&
        (0, json_1.isRecord)(part.input)) {
        for (const key of Object.keys(part.input))
            consumedKeys.add(key);
    }
    const providerMetadata = (0, json_1.remainingProviderMetadata)(inferRemainder ? [value] : [], consumedKeys, explicitMetadata);
    return providerMetadata ? { ...part, providerMetadata } : part;
}
function normalizePartValue(value, parserContext) {
    const record = (0, json_1.asRecord)(value);
    return normalizePartBase(value, parserContext).map((entry) => finalizePart(entry, value, record));
}
function finalizePart(part, value, record) {
    const normalized = record ? withProviderMetadata(part, record) : part;
    if (normalized.type === "tool-call") {
        rawToolCallKeys.set(normalized, getRawToolCallKey(value, normalized));
    }
    return normalized;
}
/**
 * Preserve the legacy ingestion identity for idless calls while the raw
 * arguments are still available. The key is kept outside the public part
 * shape so client-safe consumers never see ingestion-only compatibility data.
 */
function getToolCallKeyForPart(part) {
    return part.type === "tool-call" ? rawToolCallKeys.get(part) : undefined;
}
function getRawToolCallKey(value, part) {
    if (part.toolCallId)
        return `id:${part.toolCallId}`;
    const record = (0, json_1.asRecord)(value);
    const functionCall = (0, json_1.asRecord)(record?.function) ??
        (0, json_1.asRecord)(record?.functionCall) ??
        (0, json_1.asRecord)(record?.function_call);
    const rawArguments = functionCall?.arguments ??
        functionCall?.args ??
        functionCall?.input ??
        record?.arguments ??
        record?.args ??
        record?.input;
    let argumentsValue;
    if (typeof rawArguments === "string") {
        argumentsValue = rawArguments;
    }
    else {
        try {
            argumentsValue = JSON.stringify(rawArguments ?? {});
        }
        catch {
            argumentsValue = JSON.stringify(part.input);
        }
    }
    return `value:${part.toolName}:${argumentsValue}`;
}
